import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { ProductStock, StockRelocationRequest } from "../shared/types";

export type SortOrder = "asc" | "desc";

const stockContext = createContext<{
    stock: ProductStock[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchStock: () => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    stock: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "expires_at",
    sortOrder: "asc",
    fetchStock: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

function parseStock(d: any): ProductStock {
    return {
        id: d.id,
        product: d.product,
        location: d.location,
        level: d.level,
        quantity: d.quantity,
        weight: d.weight,
        expires_at: new Date(d.expires_at),
        shelf_life: d.shelf_life,
    };
}

function useProviderStock() {
    const [stock, setStock] = useState<ProductStock[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("expires_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchStock();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchStock() {
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(
                `/stock?${params.toString()}`
            );
            if (!response.ok) {
                setErrors([
                    'Loading stock failed. Please try again.',
                ]);
                return;
            }
            const json = await response.json();
            const data: ProductStock[] = (json.data ?? json).map(parseStock);
            setTotal(json.meta.total ?? data.length);
            setStock(data);
        } catch (error) {
            console.error(error);
            setErrors([
                'Loading stock failed. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    return {
        stock,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchStock,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    }
}

export function ProviderStock({ children }: { children: React.ReactNode }) {
    const stock = useProviderStock();
    return <stockContext.Provider value={stock}> {children}</stockContext.Provider>;
}

export const useStock = () => {
    return useContext(stockContext);
}

function parseRelocationRequest(d: any): StockRelocationRequest {
    return {
        id: d.id,
        product_stock: parseStock(d.product_stock),
        location: d.location,
        level: d.level,
        user: d.user,
        status: d.status,
        created_at: new Date(d.created_at),
    };
}

export function useStockDetail(id: string | undefined) {
    const [stockItem, setStockItem] = useState<ProductStock | null>(null);
    const [relatedStock, setRelatedStock] = useState<ProductStock[]>([]);
    const [relocationRequests, setRelocationRequests] = useState<StockRelocationRequest[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (id) fetchStockDetail();
    }, [id]);

    async function fetchStockDetail() {
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/stock/${id}`);
            if (!response.ok) {
                setErrors(['Loading stock details failed. Please try again.']);
                return;
            }
            const json = await response.json();
            const item = parseStock(json.data ?? json);
            setStockItem(item);

            const [relatedResponse, relocationResponse] = await Promise.all([
                apiService.get(`/products/${item.product.id}/stock`),
                apiService.get(`/stock/${id}/relocation-requests`),
            ]);

            if (relatedResponse.ok) {
                const relatedJson = await relatedResponse.json();
                const all: ProductStock[] = (relatedJson.data ?? relatedJson).map(parseStock);
                setRelatedStock(all.filter((s) => s.id !== item.id));
            }

            if (relocationResponse.ok) {
                const relocationJson = await relocationResponse.json();
                const requests: StockRelocationRequest[] = (relocationJson.data ?? relocationJson).map(parseRelocationRequest);
                setRelocationRequests(requests);
            }
        } catch (error) {
            console.error(error);
            setErrors(['Loading stock details failed. Please try again.']);
        } finally {
            setLoading(false);
        }
    }

    return { stockItem, relatedStock, relocationRequests, errors, loading, fetchStockDetail };
}